/* eslint-disable @next/next/no-img-element */
/* eslint-disable react/require-default-props */
/* eslint-disable react/no-danger */
import { KatAlert, KatIcon } from '@amzn/katal-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'src/redux/hooks';
import { AppState } from 'src/redux/store';
import FetchStatus from 'src/types/FetchStatus';
import { Post } from 'src/types/Search';
import { useRouter } from 'next/router';
import {
  CardType, DiscussionCard, SearchResultsCardHeader, Avatar, CardHighlight, ThreadMetrics,
} from '@amzn/cuminuicomponents';
import { getThreadDetail, getUserProfileDetail } from 'src/constants/pages';
import { SearchType, selectSearch } from 'src/redux/slices/postSearch';
import Link from 'next/link';
import { ButtonTypes, MetricKeys } from 'src/constants/metrics';
import {
  constructQueryParams, isStatusLoading, transformHtmlToReact,
  useMetrics,
} from 'src/utils';
import SearchListingSkeletonLoader from 'src/components/search-listing/SearchListingSkeletonLoader';
import { PostState, PostType } from 'src/types/DiscussionPost';
import AmazonRepliedBadge from 'public/images/icons/amazonRepliedBadge.svg';
import { SortByType } from './utils/types';

interface SearchFilters {
  sortBy: SortByType
}
export interface SearchListingContainerProps {
  type?: SearchType.threadListing | SearchType.similarDiscussions,
  showAvatar?: boolean,
  filters?: SearchFilters
}

const SearchListingContainer: React.FC<SearchListingContainerProps> = ({
  type = SearchType.threadListing,
  showAvatar = true,
  filters,
}) => {
  const { t } = useTranslation();
  const { clickMetric } = useMetrics();
  const { posts, status: fetchStatus } = useAppSelector(
    (state: AppState) => selectSearch(state, type),
  );
  const { groups } = useAppSelector((state) => state.groups);
  const { basePath } = useRouter();

  if (
    posts
    && posts.length === 0
    && (isStatusLoading(fetchStatus))
  ) {
    return (
      <div className="flex flex-auto justify-center">
        <div
          data-testid="searchlisting-container-spinner"
          className="flex flex-auto flex-col content-center items-center justify-center space-y-2.5 transparent-gradient-[180]"
        >
          {[1, 2, 3].map(
            (_) => <SearchListingSkeletonLoader key={_} showAvatar={showAvatar} />,
          )}
        </div>
      </div>
    );
  }
  if (posts && posts.length === 0 && fetchStatus === FetchStatus.complete) {
    return (
      <div className="flex flex-auto justify-center">
        <div data-testid="searchListing-no-posts" className="flex flex-col items-center justify-center space-y-2">
          {t('homePage_noPostsToDisplay')}
        </div>
      </div>
    );
  }

  const handleMetrics = (postId: string, postIndex: number) => {
    const stringMetrics = filters
    && Object.entries(filters).map(
      ([keys, values]) => ({ metricName: keys, metricValue: values }),
    );
    clickMetric({
      metricValue: postId,
      metricName: ButtonTypes.postTitle,
      countMetrics: [{ metricName: MetricKeys.postIndex, metricValue: postIndex }],
      stringMetrics,
    });
  };

  const getLinkToThread = (threadId: string, postId: string, postType: PostType) => {
    if (postType === PostType.op) {
      return getThreadDetail(threadId);
    }
    return constructQueryParams({ postId }, getThreadDetail(threadId));
  };

  const getBadges = (post: Post) => {
    const badges = [];
    if (post.contentState === PostState.closed) {
      badges.push(<KatIcon key="badge-post-locked" name="lock_outline" size="small" className="text-kat-nordic" data-testid="badge-post-locked" />);
    }
    if (post.hasAmazonReply) {
      badges.push(<img key="badge-amazon-replied" className="h-[20px] w-[20px]" src={AmazonRepliedBadge.src} alt="Amazon replied" data-testid="badge-amazon-replied" />);
    }
    if (post.hasSolution) {
      badges.push(<KatIcon key="badge-has-solution" name="check_circle" size="small" className="text-kat-fiji" data-testid="badge-has-solution" />);
    }
    return badges.length > 0 ? (
      <div className="flex flex-row space-x-[7px] rtl:space-x-reverse">
        {badges}
      </div>
    ) : undefined;
  };

  return (
    <div data-testid="searchListing-container" className="flex flex-col space-y-2.5">
      {posts && posts.map((el: Post, index: number) => {
        const authorProfileHref = el.userId && `${basePath}${getUserProfileDetail(el.userId)}`;
        return (
          <DiscussionCard
            key={el.postId}
            avatar={showAvatar ? (
              <Avatar
                src={el.userAvatarSrc}
                href={authorProfileHref}
                onClick={() => clickMetric({
                  metricValue: el.userId,
                  metricName: ButtonTypes.postAuthor,
                })}
              />
            ) : undefined}
            cardHighlight={<CardHighlight cardHighlightColorList={el.isAmazonReplied ? ['kat-persimmon'] : ['kat-lake']} />}
            cardType={CardType.search}
            content={(
              <span
                data-testid="search-card-content"
                className={`break-all images:max-w-full ${el.isMigrated ? 'discourse-forums' : ''}`}
              >
                {transformHtmlToReact({ html: el.content, metadata: el.contentMetadata })}
              </span>
              )}
            header={(
              <SearchResultsCardHeader
                authoredBy={el.userDisplayName}
                authoredByHref={authorProfileHref}
                authoredByOnClick={() => clickMetric({
                  metricValue: el.userId,
                  metricName: ButtonTypes.postAuthor,
                })}
                createdAt={el.createdAt}
                groupId={el.groupId && t(groups[el.groupId]?.nameStringId)}
                replyingToAuthor={el.replyToUserName}
                replyToHref={`${basePath}${constructQueryParams({ postId: el.replyTo }, getThreadDetail(el.threadId))}`}
                title={(
                  <Link
                    key={el.postId}
                    href={getLinkToThread(el.threadId, el.postId, el.postType)}
                    data-testid="heading"
                    role="link"
                    className="cursor-pointer break-all text-sm font-bold !text-kat-nordic line-clamp-1"
                    onClick={() => handleMetrics(el.postId, index)}
                  >
                    {el.title}
                  </Link>
                  )}
                badges={getBadges(el)}
              />
            )}
            threadMetrics={(
              <ThreadMetrics
                downvoteCount={el.downVotes}
                lastActivity={el.lastActivityTime}
                replyCount={el.totalReplies}
                upvoteCount={el.upVotes}
                viewCount={el.totalViews}
              />
              )}
          />
        );
      })}
      {fetchStatus === FetchStatus.failed
        && type === SearchType.threadListing && (
        <KatAlert
          data-testid="search-listing-failure"
          variant="warning"
          className="my-2"
          persistent
          header={t('request_threadDetailsFetchFailureHeader')}
          description={t('request_threadDetailsFetchFailureDescription')}
        />
      )}
    </div>
  );
};
export default SearchListingContainer;
